import { Breadcrumbs } from 'components/bredcrambs';
import { Layout } from 'components/layout';
import { CustomMetadata } from 'components/metadata';
import { Anchor } from 'constants/anchors';
import { IComplaint, IQuestion } from 'constants/types';
import { useLazyComplaintsQuery } from 'hooks/api/complaints';
import { useLazyQuestionsQuery } from 'hooks/api/questions';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Card } from '../question/card';
import { CommentPost } from '../question/comments';
import { WriteComment } from '../publication/write-comment';
import styles from './styles.module.css';

type TopicVariant = 'question' | 'complaint';
type TopicThreadItem = IQuestion | IComplaint;

const REPLY_ANIMATION_MS = 300;
const COMPACT_REPLY_MAX_WIDTH = 1279;

function useIsCompactReplyLayout() {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${COMPACT_REPLY_MAX_WIDTH}px)`
    );

    const update = () => setIsCompact(mediaQuery.matches);

    update();
    mediaQuery.addEventListener('change', update);

    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return isCompact;
}

interface TopicThreadPageProps {
  variant: TopicVariant;
  mainItem: TopicThreadItem;
  childrenItems: TopicThreadItem[];
  metadata: React.ComponentProps<typeof CustomMetadata>['metadata'];
  breadcrumbs: Array<{ id: number; path: string; title: string }>;
  isSearchInputMobileVisible?: boolean;
}

export const TopicThreadPage = ({
  variant,
  mainItem,
  childrenItems,
  metadata,
  breadcrumbs,
  isSearchInputMobileVisible = false,
}: TopicThreadPageProps) => {
  const router = useRouter();
  const [replyTarget, setReplyTarget] = useState<TopicThreadItem | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const isCompactReplyLayout = useIsCompactReplyLayout();
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [fetchQuestion, questionQuery] = useLazyQuestionsQuery();
  const [fetchComplaint, complaintQuery] = useLazyComplaintsQuery();

  const { data, isFetching, isError } =
    variant === 'question' ? questionQuery : complaintQuery;

  useEffect(() => {
    setIsMounted(true);
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isMounted || !isCompactReplyLayout) {
      document.body.classList.remove('reply-form-open');
      return;
    }

    if (replyTarget) {
      document.body.classList.add('reply-form-open');
    } else {
      document.body.classList.remove('reply-form-open');
    }

    return () => {
      document.body.classList.remove('reply-form-open');
    };
  }, [replyTarget, isMounted, isCompactReplyLayout]);

  const closeReplyForm = useCallback(() => {
    if (!replyTarget) {
      return;
    }

    setIsClosing(true);

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    closeTimerRef.current = setTimeout(() => {
      setReplyTarget(null);
      setIsClosing(false);
      closeTimerRef.current = null;
    }, REPLY_ANIMATION_MS);
  }, [replyTarget]);

  const handleReplyClick = (reply: TopicThreadItem) => {
    const isSameReply =
      replyTarget &&
      String(replyTarget._id) === String(reply._id) &&
      !isClosing;

    if (isSameReply) {
      closeReplyForm();
      return;
    }

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setIsClosing(false);
    setReplyTarget(reply);
    // router.push(`#${Anchor.WriteComment}`);
  };

  const handleAddComment = () => {
    if (!isFetching && !isError) {
      if (variant === 'question') {
        fetchQuestion({ parentId: mainItem._id });
      } else {
        fetchComplaint({ parentId: mainItem._id });
      }
    }

    closeReplyForm();
  };

  const threadItems =
    data && data.results.length > childrenItems.length
      ? [...data.results]
      : [...childrenItems];

  const isReplyOpen = Boolean(replyTarget) && !isClosing;

  const replyForm = replyTarget ? (
    <div
      className={clsx(styles.write_comment, {
        [styles.write_comment_overlay]: isCompactReplyLayout,
        [styles.write_comment_enter]: !isClosing,
        [styles.write_comment_closing]: isClosing,
      })}
    >
      <WriteComment
        key={String(replyTarget._id)}
        variant={variant}
        position="fixed"
        casinoId={mainItem.casino_id}
        parent={mainItem._id?.toString()}
        reply={replyTarget}
        onCommentAdded={handleAddComment}
        onCancel={closeReplyForm}
        anchor={Anchor.WriteComment}
      />
    </div>
  ) : null;

  return (
    <Layout
      hasFooter
      bgVariant="basic"
      isSearchInputMobileVisible={isSearchInputMobileVisible}
    >
      <CustomMetadata metadata={metadata} />

      <main className={styles.main}>
        <div
          className={clsx(styles.container, {
            [styles.container_reply_open]:
              isCompactReplyLayout && Boolean(replyTarget),
          })}
        >
          <Breadcrumbs lastEnable items={breadcrumbs} />

          <Card
            data={mainItem as IQuestion}
            onReplyClick={handleReplyClick}
            isActive={
              isReplyOpen && String(replyTarget?._id) === String(mainItem._id)
            }
          />

          <div className={styles.comments}>
            {threadItems.map((item) => (
              <CommentPost
                key={item._id}
                data={item as IQuestion}
                onReplyClick={handleReplyClick}
                isActive={
                  isReplyOpen && String(replyTarget?._id) === String(item._id)
                }
              />
            ))}
          </div>

          {!isCompactReplyLayout && replyForm}
        </div>

        {isMounted && isCompactReplyLayout && replyForm
          ? createPortal(replyForm, document.body)
          : null}
      </main>
    </Layout>
  );
};
